import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';

import { IApiCatalogoTabela, useApiController } from '../../../hooks/useApiController';
import { IDataNoPagination } from '../../../types/shared';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

// TTL padrão (segundos) do JWT emitido para tokens da rota. ~15 min — o backend
// aceita 60..86400 e reautentica ao expirar (espelha o default de EXTERNAL/recurso).
const JWT_TTL_SEGUNDOS_DEFAULT = 900;

// slug a partir do nome/tabela (kebab-case simples, sem acento/espaço).
// ̀-ͯ = bloco Unicode dos diacríticos combinantes (removidos após NFD),
// escrito por codepoint para não depender de caracteres combinantes literais no fonte.
const toSlug = (v: string): string =>
  v
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

export function RotaFormModal({ isOpen, onClose, onCreated }: Props) {
  const { getCatalogo, createRecurso } = useApiController();

  const [nome, setNome] = useState('');
  const [tabelasSelecionadas, setTabelasSelecionadas] = useState<string[]>([]);
  const [busca, setBusca] = useState('');

  const { data: catalogoData, isFetching } = useQuery<IDataNoPagination<IApiCatalogoTabela[]>>({
    queryKey: ['api-controller-catalogo'],
    queryFn: getCatalogo,
    enabled: isOpen,
  });
  const catalogo = catalogoData?.data ?? [];

  useEffect(() => {
    if (!isOpen) {
      setNome('');
      setTabelasSelecionadas([]);
      setBusca('');
    }
  }, [isOpen]);

  const tabelasFiltradas = useMemo(
    () =>
      busca.trim()
        ? catalogo.filter(t => t.tabela.toLowerCase().includes(busca.trim().toLowerCase()))
        : catalogo,
    [catalogo, busca],
  );

  const toggleTabela = (tabela: string) => {
    setTabelasSelecionadas(prev =>
      prev.includes(tabela) ? prev.filter(t => t !== tabela) : [...prev, tabela],
    );
  };

  const { mutateAsync, isPending } = useMutation({
    mutationFn: createRecurso,
    onSuccess: () => {
      onCreated();
      toast.success('Rota criada com sucesso', { position: 'top-right' });
      onClose();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail ?? 'Erro ao criar rota', {
        position: 'top-right',
      });
    },
  });

  const podeSalvar = nome.trim().length >= 2 && tabelasSelecionadas.length > 0;

  const handleSubmit = async () => {
    if (!podeSalvar) return;
    const slug = toSlug(nome);
    await mutateAsync({
      slug,
      nome: nome.trim(),
      // a "principal"/legada é a 1ª tabela escolhida
      tabela_fonte: tabelasSelecionadas[0],
      jwt_ttl_segundos: JWT_TTL_SEGUNDOS_DEFAULT,
      tabelas: tabelasSelecionadas.map(t => ({
        tabela_fonte: t,
        slug: toSlug(t.replace(/^(extract_|ecac_|ca_)/, '')),
      })),
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader>Criar rota da API pública</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box display="flex" flexDirection="column" gap={4}>
            <FormControl>
              <FormLabel>Nome da rota</FormLabel>
              <Input
                value={nome}
                onChange={e => setNome(e.target.value)}
                placeholder="Ex: Folha de pagamento"
              />
              {nome.trim() && (
                <Text fontSize="xs" color="gray.500" mt={1}>
                  slug: <b>{toSlug(nome)}</b> — vira /external/v1/{toSlug(nome)}/…
                </Text>
              )}
            </FormControl>

            <FormControl>
              <Flex align="center" justify="space-between" mb={1}>
                <FormLabel mb={0}>
                  Tabelas de acesso desta rota
                  {tabelasSelecionadas.length > 0 && (
                    <Badge ml={2} colorScheme="blue">
                      {tabelasSelecionadas.length} selecionada(s)
                    </Badge>
                  )}
                </FormLabel>
              </Flex>
              <Text fontSize="xs" color="gray.500" mb={2}>
                Só tabelas com vínculo de empresa (external_id) aparecem. As colunas são escolhidas
                depois, ao criar o token.
              </Text>
              <Input
                size="sm"
                mb={2}
                placeholder="Filtrar tabela…"
                value={busca}
                onChange={e => setBusca(e.target.value)}
              />
              {isFetching ? (
                <Flex justify="center" py={6}>
                  <Spinner />
                </Flex>
              ) : (
                <Box maxH="320px" overflowY="auto" borderWidth="1px" borderRadius="md" p={2}>
                  {tabelasFiltradas.map(t => (
                    <Checkbox
                      key={t.tabela}
                      size="sm"
                      display="flex"
                      py={1}
                      isChecked={tabelasSelecionadas.includes(t.tabela)}
                      onChange={() => toggleTabela(t.tabela)}
                    >
                      <Text as="span" fontSize="sm">
                        {t.tabela}
                      </Text>
                      <Text as="span" fontSize="xs" color="gray.400" ml={2}>
                        ({t.colunas.length} colunas)
                      </Text>
                    </Checkbox>
                  ))}
                  {tabelasFiltradas.length === 0 && (
                    <Text fontSize="sm" color="gray.400" p={2}>
                      Nenhuma tabela encontrada.
                    </Text>
                  )}
                </Box>
              )}
            </FormControl>
          </Box>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="outline" onClick={onClose} isDisabled={isPending}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} isLoading={isPending} isDisabled={!podeSalvar}>
            Criar rota
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
